import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { DeviceCategory, DeviceStatus } from '@/types'
import type { Focus } from '@/components/datacenter/store'

import { motion } from 'motion/react'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { Kbd, glass } from '@/components/datacenter/hud/primitives'

import {
    Database,
    LayoutGrid,
    Network,
    Play,
    Server,
    Square
} from 'lucide-react'

import {
    formatCapacity,
    formatInteger,
    toCategory,
    toStatus
} from '@/lib/inventory'

import { CATEGORY_META, STATUS_META, STATUS_ORDER } from '@/constants/inventory'

type Option = {
    focus: Focus
    label: string
    icon: LucideIcon
    color: string
    shortcut: string
}

const OPTIONS: Option[] = [
    { focus: 'overview', label: 'Overview', icon: LayoutGrid, color: '#e2e8f0', shortcut: '1' },
    { focus: 'server', label: 'Servers', icon: Server, color: CATEGORY_META.server.color, shortcut: '2' },
    { focus: 'network', label: 'Network', icon: Network, color: CATEGORY_META.network.color, shortcut: '3' },
    { focus: 'storage', label: 'Storage', icon: Database, color: CATEGORY_META.storage.color, shortcut: '4' }
]

const emptyStatus = (): Record<DeviceStatus, number> => ({ online: 0, warning: 0, critical: 0, maintenance: 0, offline: 0, unknown: 0 })

const ExploreDock: FC = (): ReactNode => {
    const focus = useDatacenterStore(state => state.focus)
    const setFocus = useDatacenterStore(state => state.setFocus)
    const touring = useDatacenterStore(state => state.touring)
    const startTour = useDatacenterStore(state => state.startTour)
    const stopTour = useDatacenterStore(state => state.stopTour)
    const { inventory, stats, layout } = useInventory()

    const breakdown = useMemo(() => {
        const result: Record<DeviceCategory, Record<DeviceStatus, number>> = { server: emptyStatus(), network: emptyStatus(), storage: emptyStatus() }

        for (const device of inventory.devices) result[toCategory(device.category)][toStatus(device.status)] += 1

        return result
    }, [inventory.devices])

    const subtitle = (option: Option): string => {
        switch (option.focus) {
            case 'overview':
                return `${formatInteger(stats.racks)} racks in ${layout.rows.length} rows`
            case 'server':
                return `${formatInteger(stats.byCategory.server)} devices, ${stats.memoryTb.toFixed(0)} TB memory`
            case 'network':
                return `${formatInteger(stats.byCategory.network)} switches, routers and links`
            default:
                return `${formatInteger(stats.byCategory.storage)} systems, ${formatCapacity(stats.storageTb)}`
        }
    }

    const statusBar = (category: DeviceCategory) => {
        const counts = breakdown[category]
        const total = STATUS_ORDER.reduce((sum, status) => sum + counts[status], 0)

        if (!total) return null

        return (
            <div className='mt-2.5 flex h-1 w-full overflow-hidden rounded-full bg-white/5' aria-hidden='true'>
                {STATUS_ORDER.map(status => counts[status] ? (
                    <div key={status} style={{ width: `${(counts[status] / total) * 100}%`, backgroundColor: STATUS_META[status].color }} />
                ) : null)}
            </div>
        )
    }

    return (
        <>
            <motion.nav
                aria-label='Explore the data hall'
                className='pointer-events-none absolute left-4 top-24 z-20 hidden w-72 md:block'
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className={cn(glass, 'pointer-events-auto p-2')}>
                    <p className='px-3 pb-2 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500'>
                        Explore the hall
                    </p>

                    <ul className='space-y-1'>
                        {OPTIONS.map((option, index) => {
                            const active = focus === option.focus
                            const Icon = option.icon

                            return (
                                <motion.li
                                    key={option.focus}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.25 + index * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <button
                                        type='button'
                                        onClick={() => setFocus(option.focus)}
                                        aria-pressed={active}
                                        className='group relative w-full overflow-hidden rounded-xl px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                                    >
                                        {active && (
                                            <motion.span
                                                layoutId='explore-active'
                                                className='absolute inset-0 rounded-xl'
                                                style={{
                                                    background: `linear-gradient(120deg, ${option.color}26, ${option.color}08 60%)`,
                                                    boxShadow: `inset 0 0 0 1px ${option.color}55, 0 0 28px -8px ${option.color}`
                                                }}
                                                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                                            />
                                        )}
                                        {active && <span className='scan-sweep pointer-events-none absolute inset-0 rounded-xl' style={{ ['--sweep' as string]: `${option.color}33` }} />}

                                        <span className='relative flex items-center gap-3'>
                                            <span
                                                className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg ring-1 transition duration-300 group-hover:scale-105', active ? 'ring-white/20' : 'ring-white/10')}
                                                style={{ backgroundColor: `${option.color}${active ? '26' : '12'}`, color: option.color }}
                                            >
                                                <Icon className={cn('size-5', active && 'icon-breathe')} aria-hidden='true' />
                                            </span>
                                            <span className='min-w-0 flex-1'>
                                                <span className='flex items-center justify-between gap-2'>
                                                    <span className={cn('font-heading text-sm font-semibold', active ? 'text-white' : 'text-slate-200')}>
                                                        {option.label}
                                                    </span>
                                                    <Kbd className='opacity-0 transition group-hover:opacity-100'>{option.shortcut}</Kbd>
                                                </span>
                                                <span className='mt-0.5 block truncate text-xs text-slate-400'>
                                                    {subtitle(option)}
                                                </span>
                                            </span>
                                        </span>

                                        {option.focus !== 'overview' && <span className='relative block pl-[52px]'>{statusBar(option.focus)}</span>}
                                    </button>
                                </motion.li>
                            )
                        })}
                    </ul>

                    <div className='mt-2 border-t border-white/10 p-1 pt-2'>
                        <button
                            type='button'
                            onClick={touring ? stopTour : startTour}
                            className={cn(
                                'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70',
                                touring ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-gradient-to-r from-cyan-400/90 to-violet-400/90 text-slate-950 hover:brightness-110'
                            )}
                        >
                            {touring ? <Square className='size-3.5 fill-current' aria-hidden='true' /> : <Play className='size-4 fill-current' aria-hidden='true' />}
                            {touring ? 'Stop guided tour' : 'Start guided tour'}
                        </button>
                    </div>
                </div>
            </motion.nav>

            <motion.nav
                aria-label='Explore the data hall'
                className='pointer-events-none absolute inset-x-0 bottom-[84px] z-20 md:hidden'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
            >
                <ul className='no-scrollbar pointer-events-auto flex gap-2 overflow-x-auto px-3'>
                    {OPTIONS.map(option => {
                        const active = focus === option.focus
                        const Icon = option.icon

                        return (
                            <li key={option.focus} className='shrink-0'>
                                <button
                                    type='button'
                                    onClick={() => setFocus(option.focus)}
                                    aria-pressed={active}
                                    className={cn(glass, 'flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition', active ? 'text-white' : 'text-slate-300')}
                                    style={active ? { boxShadow: `inset 0 0 0 1px ${option.color}88, 0 0 24px -6px ${option.color}` } : undefined}
                                >
                                    <Icon className='size-4' style={{ color: option.color }} aria-hidden='true' />
                                    {option.label}
                                </button>
                            </li>
                        )
                    })}
                    <li className='shrink-0'>
                        <button
                            type='button'
                            onClick={touring ? stopTour : startTour}
                            className={cn(glass, 'flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-cyan-200')}
                        >
                            {touring ? <Square className='size-3.5 fill-current' aria-hidden='true' /> : <Play className='size-4 fill-current' aria-hidden='true' />}
                            {touring ? 'Stop tour' : 'Tour'}
                        </button>
                    </li>
                </ul>
            </motion.nav>
        </>
    )
}

export default ExploreDock
